import React, { useEffect, useMemo, useState } from 'react';
import {
  LayoutGrid, List, Search, Timer, User, Clock, ChevronRight, RefreshCw, UserPlus, Zap,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { can, isReadOnly, roleLabel, whyBlocked } from '../../lib/hubPermissions';
import LeadIntakeDialog from './LeadIntakeDialog';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ── Canonical stages — brief §6 nine-stage pipeline. Kept static as
//    a design fallback; the component also fetches /api/hub-crm/stages
//    at mount so future stage edits happen in one place on the backend.
const STAGES = [
  { key: 'lead',           label: 'Lead',           color: '#f97316', bg: '#ffedd5', ink: '#7c2d12' },
  { key: 'discovery',      label: 'Discovery',      color: '#f59e0b', bg: '#fef3c7', ink: '#78350f' },
  { key: 'portal',         label: 'Portal',         color: '#eab308', bg: '#fef9c3', ink: '#713f12' },
  { key: 'credit',         label: 'Credit',         color: '#22c55e', bg: '#dcfce7', ink: '#166534' },
  { key: 'proposal',       label: 'Proposal',       color: '#14b8a6', bg: '#ccfbf1', ink: '#134e4a' },
  { key: 'application',    label: 'Application',    color: '#06b6d4', bg: '#cffafe', ink: '#155e75' },
  { key: 'lodged',         label: 'Lodged',         color: '#3b82f6', bg: '#dbeafe', ink: '#1e3a8a' },
  { key: 'post_lodgement', label: 'Post-Lodgement', color: '#8b5cf6', bg: '#ede9fe', ink: '#4c1d95' },
  { key: 'settlement',     label: 'Settlement',     color: '#a855f7', bg: '#f3e8ff', ink: '#581c87' },
];

// Absorbed legacy stages from the pre-brief v1 pipeline. Any pack still
// on `documents` or `approval` falls into `portal` / `lodged` for
// grouping purposes; the backend has already normalised writes.
const STAGE_ALIASES = { documents: 'portal', approval: 'lodged' };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return String(iso); }
};
const daysSince = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
};
const initials = (n) => (n || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

/**
 * JourneyTab — the CRM-wide client pipeline spanning Lead → Settlement.
 *
 * Reads from the same `/api/lender-packs` list (packs ARE the client/deal
 * record) and groups by `journey_stage`. Ships with both List and Kanban
 * views; drag-and-drop in Kanban calls the new stage-transition endpoint.
 */
export default function JourneyTab({ onOpenPack }) {
  const { token, user } = useAuth();
  const auth = useMemo(() => {
    const h = { Authorization: `Bearer ${token}` };
    if (localStorage.getItem('mcc-demo-mode') === 'on') h['X-Demo-Mode'] = 'on';
    return h;
  }, [token]);
  const currentUserId = user?.id;

  // Phase 7/8 — role-based UI chrome. All server writes are gated too.
  const canCreateLead = can(user, 'create_lead');
  const canRunAutomations = can(user, 'run_automations');
  const canDragDrop = can(user, 'drag_drop');
  const readOnly = isReadOnly(user);


  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(() => localStorage.getItem('lp-journey-q') || '');
  const [scope, setScope] = useState(() => localStorage.getItem('lp-journey-scope') || 'all');
  const [broker, setBroker] = useState(() => localStorage.getItem('lp-journey-broker') || 'all');
  const [stageFilter, setStageFilter] = useState(() => localStorage.getItem('lp-journey-stage') || 'all');
  const [view, setView] = useState(() => localStorage.getItem('lp-journey-view') || 'kanban');
  const [draggingId, setDraggingId] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [runningAuto, setRunningAuto] = useState(false);

  React.useEffect(() => { localStorage.setItem('lp-journey-view', view); }, [view]);
  React.useEffect(() => { localStorage.setItem('lp-journey-scope', scope); }, [scope]);
  React.useEffect(() => { localStorage.setItem('lp-journey-q', q); }, [q]);
  React.useEffect(() => { localStorage.setItem('lp-journey-broker', broker); }, [broker]);
  React.useEffect(() => { localStorage.setItem('lp-journey-stage', stageFilter); }, [stageFilter]);

  // Load saved filters on mount.
  React.useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${backendUrl}/api/hub-crm/saved-filters`, { headers: auth });
        if (r.ok) setSavedFilters((await r.json()).filters || []);
      } catch { /* ignore */ }
    })();
  }, [token, auth]);

  const runAutomations = async () => {
    setRunningAuto(true);
    try {
      const r = await fetch(`${backendUrl}/api/hub-crm/automations/run`, {
        method: 'POST', headers: auth,
      });
      if (!r.ok) throw new Error(await r.text());
      const stats = await r.json();
      const fired = stats.fired_by_rule || {};
      const total = Object.values(fired).reduce((s, n) => s + n, 0);
      toast.success(
        `Automations: ${total} rule fire${total !== 1 ? 's' : ''} · ${stats.tasks_upserted} new task${stats.tasks_upserted !== 1 ? 's' : ''}`,
      );
    } catch (e) { toast.error(`Automations failed — ${e.message}`); }
    finally { setRunningAuto(false); }
  };

  const saveCurrentFilter = async () => {
    const name = window.prompt('Save this view as…');
    if (!name || !name.trim()) return;
    try {
      const r = await fetch(`${backendUrl}/api/hub-crm/saved-filters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ name: name.trim(), view, scope, broker, stage: stageFilter, q }),
      });
      if (!r.ok) throw new Error(await r.text());
      const doc = await r.json();
      setSavedFilters((prev) => [doc, ...prev]);
      toast.success(`Saved as "${doc.name}"`);
    } catch (e) { toast.error(`Save failed — ${e.message}`); }
  };
  const applySavedFilter = (f) => {
    setView(f.view || 'kanban');
    setScope(f.scope || 'all');
    setBroker(f.broker || 'all');
    setStageFilter(f.stage || 'all');
    setQ(f.q || '');
    toast.success(`Applied "${f.name}"`);
  };
  const deleteSavedFilter = async (f) => {
    if (!window.confirm(`Delete saved view "${f.name}"?`)) return;
    try {
      const r = await fetch(`${backendUrl}/api/hub-crm/saved-filters/${f.id}`, { method: 'DELETE', headers: auth });
      if (!r.ok) throw new Error(await r.text());
      setSavedFilters((prev) => prev.filter((x) => x.id !== f.id));
    } catch (e) { toast.error(`Delete failed — ${e.message}`); }
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs`, { headers: auth });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPacks(data.packs || []);
    } catch (e) {
      toast.error(`Failed to load journey — ${e.message}`);
    } finally { setLoading(false); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  // Run migration for packs missing journey_stage
  const runMigration = async () => {
    setMigrating(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/journey/migrate`, { method: 'POST', headers: auth });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      toast.success(`Migration complete — ${data.updated} updated, ${data.already_set} already set`);
      load();
    } catch (e) { toast.error(`Migration failed — ${e.message}`); }
    finally { setMigrating(false); }
  };

  const transitionStage = async (packId, newStage, forceOverride = false, reason = null) => {
    if (!canDragDrop) {
      toast.error(whyBlocked(user, 'drag_drop'));
      return;
    }
    try {
      const payload = { stage: newStage };
      if (forceOverride) {
        payload.override = true;
        payload.reason = reason || 'Broker override';
      }
      const res = await fetch(`${backendUrl}/api/lender-packs/${packId}/stage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(payload),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail || err;
        toast.error(typeof d === 'string' ? d : (d.message || 'Not permitted for your role'));
        return;
      }
      if (res.status === 409) {
        const err = await res.json();
        const detail = err.detail || {};
        const missing = (detail.missing || []).map((m) => `• ${m}`).join('\n');
        if (detail.code === 'stage_gate_hard' || !detail.override_allowed) {
          toast.error(`Cannot move — hard gate.\n${missing}`, { duration: 8000 });
          return;
        }
        // Soft gate — prompt for override reason
        const r = window.prompt(
          `${detail.message}\n\n${missing}\n\nType a reason to force the move, or leave blank to cancel:`,
        );
        if (!r || !r.trim()) return;
        return await transitionStage(packId, newStage, true, r.trim());
      }
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      setPacks((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      toast.success(`Moved to ${STAGES.find((s) => s.key === newStage)?.label || newStage}${forceOverride ? ' (override)' : ''}`);
    } catch (e) { toast.error(`Move failed — ${e.message}`); }
  };

  const distinctBrokers = useMemo(() => {
    const s = new Set();
    packs.forEach((p) => { if (p.broker) s.add(p.broker); });
    return Array.from(s);
  }, [packs]);

  const filtered = useMemo(() => {
    let arr = packs;
    if (scope === 'mine' && currentUserId) {
      arr = arr.filter((p) => p.broker_id === currentUserId || p.credit_team_member_id === currentUserId || (p.assigned_user_ids || []).includes(currentUserId));
    }
    if (broker !== 'all') arr = arr.filter((p) => p.broker === broker);
    if (stageFilter !== 'all') {
      arr = arr.filter((p) => {
        let st = p.stage || p.journey_stage || 'application';
        if (STAGE_ALIASES[st]) st = STAGE_ALIASES[st];
        return st === stageFilter;
      });
    }
    if (q.trim()) {
      const query = q.trim().toLowerCase();
      arr = arr.filter((p) => `${p.client_name || ''} ${p.lender || ''} ${p.broker || ''} ${p.application_number || ''}`.toLowerCase().includes(query));
    }
    return arr;
  }, [packs, scope, currentUserId, broker, stageFilter, q]);

  // Count of packs at each stage — for the focused-view chips.
  const stageCounts = useMemo(() => {
    const counts = { all: 0 };
    STAGES.forEach((s) => { counts[s.key] = 0; });
    packs.forEach((p) => {
      let st = p.stage || p.journey_stage || 'application';
      if (STAGE_ALIASES[st]) st = STAGE_ALIASES[st];
      if (counts[st] !== undefined) counts[st] += 1;
      counts.all += 1;
    });
    return counts;
  }, [packs]);

  const grouped = useMemo(() => {
    const g = {};
    STAGES.forEach((s) => { g[s.key] = []; });
    filtered.forEach((p) => {
      // Prefer canonical stage (Phase 1+); fall back to journey_stage then
      // the legacy alias table for the two absorbed stages.
      let st = p.stage || p.journey_stage || 'application';
      if (STAGE_ALIASES[st]) st = STAGE_ALIASES[st];
      if (g[st]) g[st].push(p);
      // Anything terminal (won/lost) or otherwise unmatched is simply
      // hidden from the kanban until Phase 5 introduces terminal lanes.
    });
    return g;
  }, [filtered]);

  return (
    <div className="space-y-4">
      {/* Header + toggles */}
      <div className="rounded-2xl border bg-white p-3 flex items-center gap-2 shadow-sm flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, lender, broker…" className="h-9 pl-8 text-xs" data-testid="journey-search" />
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="journey-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            <SelectItem value="mine">My Clients</SelectItem>
          </SelectContent>
        </Select>
        <Select value={broker} onValueChange={setBroker}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="journey-broker"><SelectValue placeholder="All brokers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brokers</SelectItem>
            {distinctBrokers.map((b) => (<SelectItem key={b} value={b}>{b}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          <button onClick={() => setView('kanban')} className={`px-2.5 py-1.5 transition-colors ${view === 'kanban' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid="journey-view-kanban" title="Kanban board">
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setView('list')} className={`px-2.5 py-1.5 transition-colors ${view === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`} data-testid="journey-view-list" title="List view">
            <List className="w-3.5 h-3.5" />
          </button>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={runAutomations}
          disabled={runningAuto || !canRunAutomations}
          className="text-xs"
          data-testid="journey-run-automations"
          title={canRunAutomations ? "Run all §13 automations" : whyBlocked(user, 'run_automations')}
        >
          <Zap className={`w-3 h-3 mr-1 ${runningAuto ? 'animate-pulse text-amber-500' : ''}`} /> Run automations
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={runMigration}
          disabled={migrating}
          className="text-xs"
          data-testid="journey-migrate"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${migrating ? 'animate-spin' : ''}`} /> Back-fill stages
        </Button>
        <Button
          size="sm"
          onClick={() => setLeadOpen(true)}
          disabled={!canCreateLead}
          title={canCreateLead ? "" : whyBlocked(user, 'create_lead')}
          className="text-xs bg-slate-900 hover:bg-slate-800 disabled:opacity-40"
          data-testid="journey-new-lead"
        >
          <UserPlus className="w-3 h-3 mr-1" /> New lead
        </Button>
        {/* Role badge — makes it obvious which permission scope is active */}
        <span
          className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 border"
          title={`Signed in as ${roleLabel(user)}`}
          data-testid="journey-role-badge"
        >
          {roleLabel(user)}{readOnly ? ' · read-only' : ''}
        </span>
      </div>

      {/* ── Focused-view chip strip (Phase 5, brief §17 #7) ────
          Quick presets that filter the same pipeline to a single
          stage bucket — Leads, Active, Credit, Ready to Lodge, etc.
          Filter state persists across view toggles so switching
          Kanban ↔ List doesn't lose your focused view.              */}
      <div className="rounded-2xl border bg-white p-2 flex items-center gap-1.5 shadow-sm flex-wrap" data-testid="journey-focus-strip">
        <FocusChip label="Everything" count={stageCounts.all} active={stageFilter === 'all'} onClick={() => setStageFilter('all')} testid="focus-all" />
        {STAGES.map((s) => (
          <FocusChip
            key={s.key}
            label={s.label}
            count={stageCounts[s.key] || 0}
            active={stageFilter === s.key}
            onClick={() => setStageFilter(s.key)}
            testid={`focus-${s.key}`}
            color={s.color}
          />
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          {savedFilters.length > 0 && (
            <Select value="" onValueChange={(v) => {
              const f = savedFilters.find((x) => x.id === v);
              if (f) applySavedFilter(f);
            }}>
              <SelectTrigger className="h-7 text-[11px] px-2 w-40" data-testid="journey-saved-filters">
                <SelectValue placeholder="Saved views…" />
              </SelectTrigger>
              <SelectContent>
                {savedFilters.map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`saved-filter-${f.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">{f.name}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteSavedFilter(f); }}
                        className="text-slate-400 hover:text-rose-600 text-[10px] ml-2"
                        title="Delete saved view"
                      >×</button>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="sm" onClick={saveCurrentFilter} className="text-[11px] h-7 px-2" data-testid="journey-save-view">
            Save view
          </Button>
        </div>
      </div>

      <LeadIntakeDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        onCreated={(deal) => {
          setPacks((prev) => [deal, ...prev]);
          onOpenPack?.(deal.id);
        }}
      />

      {loading ? (
        <p className="text-sm text-slate-500 py-10 text-center">Loading journey…</p>
      ) : view === 'kanban' ? (
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1" data-testid="journey-kanban">
          {STAGES.map((s) => (
            <StageColumn
              key={s.key}
              stage={s}
              packs={grouped[s.key] || []}
              draggingId={draggingId}
              onDragStart={(id) => setDraggingId(id)}
              onDragEnd={() => setDraggingId(null)}
              onDrop={(packId) => transitionStage(packId, s.key)}
              onOpen={onOpenPack}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border bg-white shadow-sm overflow-x-auto" data-testid="journey-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">Stage</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Lender</th>
                <th className="px-3 py-2 text-left">Broker</th>
                <th className="px-3 py-2 text-left">CSM / Credit</th>
                <th className="px-3 py-2 text-left">Last activity</th>
                <th className="px-3 py-2 text-left">Days in stage</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const s = STAGES.find((x) => x.key === (p.journey_stage || 'application')) || STAGES[6];
                return (
                  <tr key={p.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onOpenPack && onOpenPack(p.id)} data-testid={`journey-row-${p.id}`}>
                    <td className="px-3 py-2">
                      <p className="font-black text-slate-900 text-[12.5px] leading-tight">{p.client_name}</p>
                      <p className="text-[10.5px] text-slate-500">{p.application_number || '—'}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: s.bg, color: s.ink }}>{s.label}</span>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-700">{(p.status || '').replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-700">{p.lender || '—'}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-700">{p.broker || '—'}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-700">{p.credit_team_member || '—'}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500 tabular-nums">{fmtDate(p.last_updated_at)}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500 tabular-nums">{daysSince(p.journey_stage_updated_at || p.last_updated_at)}d</td>
                    <td className="px-3 py-2 text-right"><ChevronRight className="w-4 h-4 text-slate-300 inline" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StageColumn({ stage, packs, draggingId, onDragStart, onDragEnd, onDrop, onOpen }) {
  const [isOver, setIsOver] = useState(false);
  return (
    <div
      className="w-[280px] flex-shrink-0"
      onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        const id = e.dataTransfer.getData('text/plain');
        if (id) onDrop(id);
      }}
    >
      <div className="rounded-t-xl border border-b-0 px-3 py-2 flex items-center gap-2"
        style={{ background: isOver ? stage.color : stage.bg, borderColor: stage.color, color: stage.ink }}>
        <div className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
        <h3 className="text-[11px] font-black uppercase tracking-widest flex-1" style={{ color: isOver ? '#ffffff' : stage.ink }}>{stage.label}</h3>
        <span className="text-[10px] font-black tabular-nums" style={{ color: isOver ? '#ffffff' : stage.ink }}>{packs.length}</span>
      </div>
      <div className="rounded-b-xl border p-2 space-y-2 min-h-[140px] max-h-[calc(100vh-260px)] overflow-y-auto bg-white"
        style={{ borderColor: stage.color, boxShadow: isOver ? `inset 0 0 0 2px ${stage.color}` : 'none' }}>
        {packs.length === 0 ? (
          <div className="text-center py-4 text-[10px] italic text-slate-400">Drop here</div>
        ) : packs.map((p) => (
          <PackCard key={p.id} pack={p} stage={stage}
            dragging={draggingId === p.id}
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', p.id); onDragStart(p.id); }}
            onDragEnd={onDragEnd}
            onOpen={() => onOpen && onOpen(p.id)} />
        ))}
      </div>
    </div>
  );
}

function PackCard({ pack, stage, dragging, onDragStart, onDragEnd, onOpen }) {
  const daysHere = daysSince(pack.journey_stage_updated_at || pack.last_updated_at);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className={`rounded-lg border bg-white p-2.5 cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${dragging ? 'opacity-50' : ''}`}
      style={{ borderColor: '#e2e8f0' }}
      data-testid={`journey-card-${pack.id}`}
    >
      <p className="text-[12.5px] font-black text-slate-900 leading-tight truncate">{pack.client_name}</p>
      {pack.lender && <p className="text-[10.5px] font-semibold text-slate-500 truncate">{pack.lender}</p>}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {pack.broker && (
          <span className="inline-flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5 rounded" style={{ background: '#f1f5f9', color: '#334155' }}>
            <span className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7.5px] font-black text-white bg-indigo-600">{initials(pack.broker)}</span>
            {pack.broker.split(' ')[0]}
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-[9.5px] font-black tabular-nums px-1.5 py-0.5 rounded"
          style={{ background: daysHere > 5 ? '#fee2e2' : '#f1f5f9', color: daysHere > 5 ? '#991b1b' : '#334155' }}>
          <Timer className="w-2 h-2" /> {daysHere}d in stage
        </span>
        {pack.completion != null && (
          <span className="text-[9.5px] font-black tabular-nums text-slate-500">{pack.completion}%</span>
        )}
      </div>
    </div>
  );
}


// ── FocusChip (Phase 5) ─────────────────────────────────────
// A single clickable pill in the focused-view strip. Shows the stage
// label and the pack count for that stage; active state gets a filled
// background matching the stage colour.
function FocusChip({ label, count, active, onClick, testid, color }) {
  const style = active
    ? { background: color || '#0f172a', color: '#fff', borderColor: color || '#0f172a' }
    : {};
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={style}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
        active
          ? 'shadow-sm'
          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
      }`}
    >
      <span className="font-semibold">{label}</span>
      <span className={`text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded-full ${active ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-600'}`}>{count}</span>
    </button>
  );
}

